/**
 * What to tell an author when the question form will not open.
 *
 * A refusal is surfaced, never swallowed. The form opens a question only if it can represent
 * every part of it, and when it cannot the author gets the raw XML editor instead — which is
 * exactly what they had before this feature existed, so it is not a regression. What WOULD be
 * a regression is getting the textarea and not knowing why: a screen that silently degrades
 * teaches an author that the product is arbitrary.
 *
 * TWO SENTENCES, WITH DIFFERENT JOBS. The server sends a `detail` that names the construct it
 * found — `<stringresponse>`, `points`, `partial_credit` — written for a person and specific
 * to the question in front of them. This file supplies the headline: what CLASS of thing went
 * wrong, in the author's own vocabulary rather than OLX's. Neither is sufficient alone. The
 * headline without the detail cannot say which construct; the detail without the headline asks
 * an academy administrator to know what a `<choicehint>` is.
 *
 * `REFUSAL_HEADLINES` is a `Record` over the reason union, so a reason added to
 * `questions.REASONS` on the server and mirrored into `QuestionRefusalReason` without a
 * sentence here FAILS THE TYPE CHECK. That is the point of keeping the set closed on both
 * sides — the spec asks that a new reason cannot be added without a case, and on this side a
 * compile error is the cheapest possible enforcement.
 *
 * `headlineFor` still tolerates a reason it has never heard of, which is not a contradiction
 * of the above: the type check binds a build, and a deployed client can be older than the
 * server it is talking to. In that window the author gets the server's own sentence and a
 * truthful frame around it, rather than an empty banner.
 */

import type { QuestionRefusalReason } from './editor';

/**
 * The headline per reason, in an author's words.
 *
 * Deliberately not a restatement of the tag. "This is a kind of question the form cannot show
 * yet" is what an administrator needs to decide what to do next; `<optionresponse>` is what
 * they need if they then ask someone. The server's `detail` carries the second, so these carry
 * the first.
 */
export const REFUSAL_HEADLINES: Record<QuestionRefusalReason, string> = {
  unparseable: 'The XML in this question is not valid, so it cannot be read as a form.',
  not_a_problem: 'This component does not contain a question.',
  no_response: 'This question has no answer section.',
  multiple_responses: 'This component holds more than one question.',
  unsupported_response: 'This is a kind of question the form cannot show yet.',
  stray_content: 'There is content beside the question that the form has nowhere to put.',
  unsupported_group: 'The answers use a layout this kind of question does not allow.',
  group_attribute: 'The answers carry a setting the form cannot show.',
  response_attribute: 'The question carries a setting the form cannot show.',
  choice_attribute: 'One of the options carries a setting the form cannot show.',
  partial_choice: 'This question awards partial credit for particular options.',
  markup_in_text: 'The question or one of its options contains formatting the form cannot edit.',
  per_option_feedback: 'This question shows its own feedback for particular options.',
  unsupported_solution: 'This question’s explanation is written in a shape the form cannot edit.',
  demand_hint: 'This question offers hints, which the form cannot edit.',
  script: 'This question runs a script to build itself.',
  comment: 'This question contains a comment, and opening it in the form would lose it.',
  markdown_present:
    'This question also keeps a Markdown version of itself, and the form would put the two out of step.',
  no_options: 'This question’s answer section has no options in it.',
  library_component: 'The question form is not available for library components yet.',
  platform_contract: 'The question form is switched off on this platform.',
};

/**
 * The one sentence to show above the raw editor.
 *
 * The fallback is deliberately not an apology and not "an unknown error". It says the true
 * thing — the form cannot open this one — and leaves the server's `detail` to say what was
 * found, because that sentence is generated from the construct itself and is therefore
 * useful even to a client that has never heard of the reason.
 */
export function headlineFor(reason: string): string {
  return (
    REFUSAL_HEADLINES[reason as QuestionRefusalReason] ??
    'The form cannot open this question.'
  );
}

/**
 * Whether a refusal is about THIS question or about the whole install.
 *
 * Worth distinguishing, because the two ask different things of the reader. "This question
 * uses a construct the form cannot show" is a fact about one component and the answer is to
 * edit the XML. "The form is switched off on this platform" is a fact about the deployment,
 * the same for every question, and editing the XML is the answer for all of them — so telling
 * an author to check this particular question would send them looking for a fault that is not
 * there.
 */
export function isInstallWide(reason: string): boolean {
  return reason === 'platform_contract' || reason === 'library_component';
}
